import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import { useNavigate } from "react-router";
import { InviteUserFormConfig, inviteUserFormConfig } from "./invite-user-form.config";
import { FormField } from "@/components/ui/form-field";
import { useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Mail } from "lucide-react";
import { useInviteUser } from "@/api/user.api";

export const InviteUser = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const { mutate: inviteUser } = useInviteUser();

    const form = useForm<InviteUserFormConfig>({
        resolver: zodResolver(inviteUserFormConfig),
        defaultValues: {
            email: "",
        },
        mode: "onChange",
    });

    const onSubmit = useCallback(
        (data: InviteUserFormConfig) => {
            inviteUser({
                email: data.email,
                tenantId: selectedTenant?.id ?? -1,
            });

            form.reset();
            navigate("..");
        },
        [selectedTenant?.id, inviteUser, form, navigate],
    );

    return (
        <Dialog open={true} onOpenChange={(val) => (!val ? navigate("..") : null)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t("users.invite")}</DialogTitle>
                    <DialogDescription>
                        <span style={{ whiteSpace: "pre-line" }}>
                            <Trans
                                i18nKey="users.invite.description"
                                values={{ organization: selectedTenant?.name }}
                            />
                        </span>
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <FormField
                        label={t("users.email")}
                        registration={{ ...form.register("email") }}
                        error={
                            form.formState.errors.email
                                ? t(form.formState.errors.email?.message as string)
                                : undefined
                        }
                        touched={form.formState.touchedFields.email}
                    />

                    <Button
                        type="submit"
                        className="w-full mt-6"
                        disabled={!form.formState.isValid}
                    >
                        <Mail className="w-4 h-4 mr-2" />
                        {t("users.invite.submit")}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
};
